import { untracked, useComputed } from "@suse-o11y/signal";
import React from "react";
import { useSelector } from "react-redux";

import type { IdOrIdentifier } from "~/tools/id-or-identifier";

import { selectTimeRange } from "../../../../app/state/view/timeline/TimelineSelectors";
import { Api } from "../../../../common/openapi";
import { useUserProfile } from "../../../../common/use-user-profile";
import { initDashboardVariablesState } from "../../../../components/_dashboard-variables-drawer";
import {
	DashboardYamlDrawer,
	type DashboardYamlDrawerState,
	initDashboardYamlEditDrawerState,
} from "../../../../components/_dashboard-yaml-drawer";
import { initSetupDashboardFormState } from "../../../../components/_setup-dashboard-drawer";
import { useExplorationDrawerState } from "../../../../components/react/drawer/use-exploration-drawer-state";
import { type SaveAsButtonAction, SaveButton } from "../../../../components/react/save/SaveButton";
import { type DashboardReadFullSchema, queryFullDashboard } from "../../../../queries/dashboards";
import { useQuery } from "../../../../queries/react-query";
import { useDashboardsPermissions } from "../../permissions";
import { useDashboardContext } from "../_dashboard-context";
import { dashboardFormOutputToSpec } from "../_dashboard-form-state";
import { useSaveDashboard } from "../_use-save-dashboard";

import { useDeleteDashboard } from "./_use-delete-dashboard";

export interface DashboardSaveButtonProps {
	idOrIdentifier: IdOrIdentifier;
}

export const DashboardSaveButton: React.FC<DashboardSaveButtonProps> = ({ idOrIdentifier }) => {
	const [yamlDrawerState, setYamlDrawerState] = React.useState<DashboardYamlDrawerState>();

	const timeRange = useSelector(selectTimeRange);
	const { state } = useDashboardContext();
	const explorationDrawer = useExplorationDrawerState();
	const [userProfile] = useUserProfile();

	const { data: dashboardSchema } = useQuery(queryFullDashboard(idOrIdentifier));

	const isDashboardDirty = useComputed((monitor) => state.form.isDirty(monitor), [state]);

	const saveDashboard = useSaveDashboard();

	const deleteDashboard = useDeleteDashboard({
		onConfirm: () => {
			// clear the state after successful deletion
			state.clear(idOrIdentifier);
		},
	});

	const { hasCreateDashboardsPermission, hasUpdateDashboardsPermission, hasDeleteDashboardsPermission } =
		useDashboardsPermissions().getOrElse({
			hasCreateDashboardsPermission: false,
			hasUpdateDashboardsPermission: () => false,
			hasDeleteDashboardsPermission: () => false,
		});

	return (
		<>
			<SaveButton
				reset={{
					enabler: isDashboardDirty,
					title: "Reset dashboard",
					handler: () => {
						state.form.reset();
					},
				}}
				rest={[
					{
						enabler: dashboardSchema,
						title: "Edit dashboard variables",
						handler: (schema) => {
							untracked((monitor) => {
								explorationDrawer.openDashboardVariables({
									id: `${schema.id}_variables`,
									timeRange,
									variables: initDashboardVariablesState(state.form.fields.variables.getInput(monitor)),
									onSave: (variables) => {
										state.form.fields.variables.setInput(variables);
									},
								});
							});
						},
					} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
					{
						enabler: dashboardSchema,
						title: "Edit dashboard properties",
						handler: (schema) => {
							explorationDrawer.openSetupDashboard({
								type: "edit",
								canSave: hasUpdateDashboardsPermission(schema),
								id: schema.id,
								form: initSetupDashboardFormState({
									name: schema.name,
									description: schema.description,
									starred: userProfile?.starredDashboards.has(schema.id) ?? false,
									publiclyAccessible: schema.scope === Api.DashboardScope.PublicDashboard,
								}),
							});
						},
					} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
					{
						enabler: dashboardSchema != null,
						title: "Edit dashboard YAML",
						handler: () => {
							untracked((monitor) => {
								const output = state.form.getOutput(monitor);

								if (output != null) {
									const spec = dashboardFormOutputToSpec(output);

									setYamlDrawerState(initDashboardYamlEditDrawerState(spec));
								}
							});
						},
					},
					{
						enabler: dashboardSchema != null && hasDeleteDashboardsPermission(dashboardSchema) && dashboardSchema,
						title: "Delete dashboard",
						handler: (schema) => {
							deleteDashboard(schema.id, schema.name);
						},
					} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
				]}
				save={{
					enabler: isDashboardDirty && dashboardSchema != null && hasUpdateDashboardsPermission(dashboardSchema),
					title: "Save dashboard",
					isPrimary: true,
					handler: () => {
						untracked((monitor) => {
							const output = state.form.getOutput(monitor);

							if (output != null) {
								const next = dashboardFormOutputToSpec(output);

								saveDashboard({ idOrIdentifier, revision: next });
							}
						});
					},
				}}
				saveAs={
					{
						enabler: hasCreateDashboardsPermission && dashboardSchema,
						title: "Save dashboard as...",
						isPrimary: isDashboardDirty,
						handler: (schema) => {
							untracked((monitor) => {
								const output = state.form.getOutput(monitor);

								if (output != null) {
									explorationDrawer.openSetupDashboard({
										type: "clone",
										id: schema.id,
										spec: dashboardFormOutputToSpec(output),
										form: initSetupDashboardFormState({
											name: schema.name,
											description: schema.description,
											starred: false,
											publiclyAccessible: false,
										}),
									});
								}
							});
						},
					} satisfies SaveAsButtonAction<DashboardReadFullSchema>
				}
			/>

			<DashboardYamlDrawer
				id={`${dashboardSchema?.id}_yaml`}
				onClose={() => {
					setYamlDrawerState(undefined);
				}}
				onSave={(spec) => {
					state.form.setInput({
						panels: spec.panels,
						variables: spec.variables,
						settings: {
							refreshInterval: spec.refreshInterval,
							timeRange: spec.timeRange,
							saveVariables: spec.saveVariables,
						},
					});
				}}
				when={yamlDrawerState}
			/>
		</>
	);
};
